import { useLanguage } from "@/i18n";
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Crosshair, Pencil, Save, Search, Undo2, X } from "lucide-react";
import { deleteFeature, downloadShapefile, listEditableLayerRows, setFeatureRevisit, updateFeature } from "@/lib/api";
import type { DataScope } from "@/lib/api";
import type { EditableLayerRow, EditableLayerSummary, FieldFeature } from "@/lib/types";
import { propertyLabel } from "@/lib/label";
import { ancestryOf, EMPTY_HIERARCHY, type Hierarchy } from "@/lib/hierarchyFilter";
import { Banner, Button, ConfirmDialog, EmptyState, Panel, Spinner, StatusBadge } from "./ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { getStoredUser } from "@/lib/auth";
import { isRevisitActive } from "@/lib/revisit";
import { DISPLAY_TIME_ZONE, parseApiDate } from "@/lib/format";

// Written by the field app into the attribute bag; shown as one status column, not four.
const REVISIT_KEYS = new Set([
  "revisit_flagged_at",
  "revisit_flagged_by",
  "revisit_reason",
  "revisit_resolved_at",
]);

// Editable-layer attributes that mean the same as a fixed column, compared
// ignoring case, spaces and underscores ("PROP_NO", "Plot No"). Their values
// go in that column instead of repeating as a column of their own.
const normKey = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, "");
const PROPERTY_NO_KEYS = new Set(["propno", "propertyno", "propertynumber", "houseno", "housenumber"]);
const PLOT_NO_KEYS = new Set(["plotno", "plotnumber"]);
function layerValue(properties: Record<string, unknown>, keys: Set<string>): string {
  for (const [k, v] of Object.entries(properties)) {
    if (keys.has(normKey(k)) && v != null && String(v).trim() !== "") return String(v);
  }
  return "—";
}

type TableView = "all" | "field" | "layers";

// The survey box's search starts on Property no., as the search always did.
const FIELD_DEFAULT_COLUMN = "property_no";

/** A survey property's text in the column picked for the search. */
function fieldSearchText(f: FieldFeature, column: string): string {
  if (column === "") {
    // Every column at once.
    return [
      fieldSearchText(f, "property_no"),
      f.partition ?? "",
      f.created_by ?? "",
      ...Object.keys(f.properties || {}).map((k) => fieldSearchText(f, k)),
    ].join("\n");
  }
  if (column === "property_no") {
    // The number and the combined label the column and the field app show, e.g. "12/A".
    return `${f.property_no ?? ""}\n${propertyLabel(f.property_no, f.partition)}`;
  }
  if (column === "partition") return f.partition ?? "";
  if (column === "created_by") return f.created_by ?? "";
  const v = f.properties?.[column];
  if (v === null || v === undefined) return "";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Start of a date-box day ("2026-10-28") as an instant. Days are India days,
 * the ones the table's times are shown in (DISPLAY_TIME_ZONE); the database
 * stores UTC, so 28 Oct starts at 27 Oct 18:30 UTC.
 */
function istDayStart(day: string): Date {
  return new Date(`${day}T00:00:00+05:30`);
}

/** The instant after a date-box day ends: the next India day's start. */
function istDayEnd(day: string): Date {
  return new Date(istDayStart(day).getTime() + 24 * 60 * 60 * 1000);
}

function formatDateTime(value: unknown): string | null {
  if (typeof value !== "string" || !ISO_DATETIME.test(value)) return null;
  const d = parseApiDate(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-IN", {
    timeZone: DISPLAY_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatPropertyDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || String(value).trim() === "") return "—";
  return formatDateTime(value) ?? (typeof value === "object" ? JSON.stringify(value) : String(value));
}

function RevisitCell({ properties }: { properties: Record<string, unknown> | null | undefined }) {
  const flaggedAt = properties?.revisit_flagged_at;
  const resolvedAt = properties?.revisit_resolved_at;
  const reason = String(properties?.revisit_reason ?? "").trim();
  const flaggedBy = String(properties?.revisit_flagged_by ?? "").trim();
  if (!flaggedAt && !resolvedAt) return <span className="text-ink-muted">—</span>;

  const resolved = Boolean(resolvedAt);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="max-w-[14rem] cursor-default">
          <StatusBadge tone={resolved ? "good" : "warning"} label={resolved ? "Resolved" : "Revisit"} />
          {reason && <div className="mt-0.5 truncate text-2xs text-ink-muted">{reason}</div>}
        </div>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs space-y-0.5 leading-snug">
        {reason && <div className="font-medium">{reason}</div>}
        {Boolean(flaggedAt) && (
          <div>Flagged {displayValue(flaggedAt)}{flaggedBy && ` by ${flaggedBy}`}</div>
        )}
        {resolved && <div>Resolved {displayValue(resolvedAt)}</div>}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Types an edited cell the way the value it replaces was typed.
 *
 * Numeric only when the stored value was already a number. Parsing anything
 * that merely LOOKS numeric would quietly rewrite identifiers: a plot number
 * "0012" becomes 12, and "1/2" would survive only because it fails to parse
 * at all. Attributes here come from municipal registers where a leading zero
 * is part of the value.
 */
function coerceLike(previous: unknown, text: string): unknown {
  if (typeof previous === "number") {
    const asNum = text.trim() === "" ? NaN : Number(text);
    return Number.isFinite(asNum) ? asNum : text;
  }
  return text;
}

/** Pending edits for one row: the two fixed fields, plus attribute cells. */
type RowEdits = {
  property_no?: string;
  partition?: string;
  properties?: Record<string, string>;
};

export default function FeatureTable({
  scope,
  features: allFeatures,
  onChanged,
  locked = false,
  lockedReason,
  onZoomTo,
  selectedId = null,
  exportScopeNote,
  hierarchy = EMPTY_HIERARCHY,
  layers = [],
  onZoomToLayerShape,
  selectedLayerShape = null,
}: {
  /** What Export downloads: the project, narrowed to the filter bar's zone /
   *  ward, or one block. */
  scope: DataScope & { zoneId?: number; wardId?: number };
  features: FieldFeature[];
  onChanged: () => void;
  /**
   * Frame this row's shape on the map.
   *
   * Optional because this table is also rendered on the block workspace, where
   * there may be no map beside it to drive; the column is simply omitted then
   * rather than showing a button that does nothing.
   */
  onZoomTo?: (feature: FieldFeature) => void;
  /** The row currently framed on the map, kept marked so it stays findable in
   *  a long table after the map has moved. */
  selectedId?: number | null;
  /**
   * Warning shown beside Export when what it produces is wider than what the
   * table is showing.
   *
   * Export is a server-side call scoped by project or by one block, so it
   * cannot follow a zone or ward filter. Downloading every shape in the
   * project from a table filtered to one ward, with nothing saying so, is the
   * kind of quiet mismatch someone only finds after sending the file on.
   */
  exportScopeNote?: string;
  /**
   * Zones/wards/blocks, for naming each row's Zone / Ward / Block columns.
   *
   * Optional and defaulted: the block workspace renders this table already
   * scoped to one block, where three columns repeating that block's own name
   * on every row would be noise. Omitting it leaves those cells showing an
   * em dash rather than breaking.
   */
  hierarchy?: Hierarchy;
  /**
   * Survey data frozen — Delete and editing are offered but disabled.
   *
   * Set for a submitted block, whose shapes the backend refuses to change for
   * anyone until it is reopened. The button used to be live and simply returned
   * 409, so the only way to learn the block was locked was to press Delete and
   * read the error.
   */
  locked?: boolean;
  lockedReason?: string;
  /**
   * Editable layers whose shapes are listed after the survey properties, cut
   * to the same zone / ward / block as `scope` and badged "Editable layer".
   * Read-only rows, fetched a page at a time from the server (a city-wide
   * layer is tens of thousands of shapes). Omitted on the block workspace.
   */
  layers?: EditableLayerSummary[];
  onZoomToLayerShape?: (layerId: number, position: number) => void;
  selectedLayerShape?: { layerId: number; position: number } | null;
}) {
  const { t } = useLanguage();
  // Which box the table lists: the survey properties, the editable-layer
  // shapes, or All (survey rows first, then the layers', as one list). The
  // date and surveyor filters apply to each.
  const [view, setView] = useState<TableView>("field");
  const showingLayers = view === "layers" && layers.length > 0;
  const combined = view === "all" && layers.length > 0;
  // Search within one column, picked beside the box. Applied on top of
  // whatever the page already filtered (zone / ward / block), so everything
  // below -- paging, the count, editing, the empty state -- works on
  // `features`, the rows that match. Only the box on show is searched.
  const [search, setSearch] = useState("");
  const [searchColumn, setSearchColumn] = useState(FIELD_DEFAULT_COLUMN);
  const query = search.trim().toLowerCase();
  const fieldQuery = showingLayers ? "" : query;
  function switchView(next: TableView) {
    if (next === view) return;
    setView(next);
    setSearch("");
    setSearchColumn(next === "field" ? FIELD_DEFAULT_COLUMN : "");
  }
  // The map picked a survey property: show a box listing it so the row can be found.
  useEffect(() => {
    if (selectedId !== null) setView((v) => (v === "layers" ? "field" : v));
  }, [selectedId]);
  // "Marked for revisit" checkbox beside the search: lists only the properties
  // currently marked for a revisit. Combines with the search.
  const [revisitOnly, setRevisitOnly] = useState(false);
  const revisitTotal = useMemo(
    () => allFeatures.filter((f) => isRevisitActive(f.properties)).length,
    [allFeatures]
  );
  // Date range: survey properties by when they were created, editable-layer
  // shapes (on the server) by when they were last updated. Either end may be
  // empty; "To" includes that whole day.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const dated = Boolean(dateFrom || dateTo);
  const fromAt = dateFrom ? istDayStart(dateFrom) : null;
  const toAt = dateTo ? istDayEnd(dateTo) : null;
  const updatedFrom = fromAt?.toISOString() ?? null;
  const updatedTo = toAt?.toISOString() ?? null;
  // Surveyor: survey properties by who collected them, editable-layer shapes
  // (on the server) by who last changed them. "" = everyone.
  const [surveyor, setSurveyor] = useState("");
  const surveyors = useMemo(
    () =>
      Array.from(new Set(allFeatures.map((f) => (f.created_by ?? "").trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [allFeatures]
  );
  const features = useMemo(() => {
    let list = allFeatures;
    if (revisitOnly) list = list.filter((f) => isRevisitActive(f.properties));
    if (surveyor) list = list.filter((f) => (f.created_by ?? "").trim() === surveyor);
    if (updatedFrom || updatedTo) {
      const from = updatedFrom ? Date.parse(updatedFrom) : -Infinity;
      const to = updatedTo ? Date.parse(updatedTo) : Infinity;
      list = list.filter((f) => {
        if (!f.created_at) return false;
        const at = parseApiDate(f.created_at).getTime();
        return at >= from && at < to;
      });
    }
    if (!fieldQuery) return list;
    return list.filter((f) => fieldSearchText(f, searchColumn).toLowerCase().includes(fieldQuery));
  }, [allFeatures, fieldQuery, searchColumn, revisitOnly, updatedFrom, updatedTo, surveyor]);
  const narrowed = Boolean(fieldQuery) || revisitOnly || dated || Boolean(surveyor);
  // Attribute keys the survey properties carry, for the search's column list.
  const surveyKeys = useMemo(() => {
    const keys = new Set<string>();
    allFeatures.forEach((f) =>
      Object.keys(f.properties || {}).forEach((k) => {
        if (!REVISIT_KEYS.has(k) && k !== "plot_no" && k !== "survey_name") keys.add(k);
      })
    );
    return Array.from(keys).sort();
  }, [allFeatures]);
  const updatedBy = surveyor || null;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // --- editable-layer rows, the second box ---
  // Searched on the server (the picked attribute, or any), settled for a
  // moment first since each change is a request. "Marked for revisit" is a
  // survey-only idea and does not touch them.
  const [layerQuery, setLayerQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setLayerQuery(showingLayers || combined ? search.trim() : ""), 300);
    return () => clearTimeout(id);
  }, [search, showingLayers, combined]);
  // All's Property no. / Plot no. mean whatever each layer calls them.
  const layerColumn = showingLayers
    ? searchColumn
    : combined
      ? searchColumn === "property_no"
        ? "@property_no"
        : searchColumn === "plot_no"
          ? "@plot_no"
          : searchColumn
      : "";
  const layerKey = layers.map((l) => `${l.id}:${l.updated_at}`).join(",");
  const [layerInfo, setLayerInfo] = useState<{ totals: Record<number, number>; columns: string[] }>({
    totals: {},
    columns: [],
  });
  const [layerError, setLayerError] = useState<string | null>(null);
  useEffect(() => {
    if (layers.length === 0) {
      setLayerInfo({ totals: {}, columns: [] });
      return;
    }
    let cancelled = false;
    Promise.all(
      layers.map((l) =>
        listEditableLayerRows(scope.projectId ?? l.project_id, l.id, {
          zoneId: scope.zoneId,
          wardId: scope.wardId,
          blockId: scope.blockId,
          q: layerQuery,
          qColumn: layerColumn,
          updatedFrom,
          updatedTo,
          updatedBy,
          offset: 0,
          limit: 1,
        })
      )
    )
      .then((pages) => {
        if (cancelled) return;
        const totals: Record<number, number> = {};
        const columns: string[] = [];
        pages.forEach((d, i) => {
          totals[layers[i].id] = d.total;
          for (const c of d.columns) if (!columns.includes(c)) columns.push(c);
        });
        setLayerError(null);
        setLayerInfo({ totals, columns });
      })
      .catch(() => {
        if (cancelled) return;
        setLayerError(t.layerRowsTable.loadFailed);
        setLayerInfo({ totals: {}, columns: [] });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerKey, scope.projectId, scope.zoneId, scope.wardId, scope.blockId, layerQuery, layerColumn, updatedFrom, updatedTo, updatedBy]);
  const layerTotal = layers.reduce((n, l) => n + (layerInfo.totals[l.id] ?? 0), 0);
  // In All, "Marked for revisit" is survey-only, so it leaves the layer rows out.
  const layersListed = showingLayers || (combined && !revisitOnly);
  const grandTotal = showingLayers ? layerTotal : features.length + (layersListed ? layerTotal : 0);

  // Back to page 1 whenever the list changes (filter applied, data refreshed, box switched).
  useEffect(() => { setPage(1); }, [features, layerInfo, view]);

  // Auto-jump to the page containing the selected feature so it is always visible.
  useEffect(() => {
    if (selectedId === null || showingLayers) return;
    const idx = features.findIndex((f) => f.id === selectedId);
    if (idx === -1) return;
    setPage(Math.floor(idx / pageSize) + 1);
  }, [selectedId, features, pageSize, showingLayers]);

  const pageCount = Math.max(1, Math.ceil(grandTotal / pageSize));
  const pagedFeatures = showingLayers ? [] : features.slice((page - 1) * pageSize, page * pageSize);

  // The layer rows falling on this page: each layer's in turn (after the
  // survey rows in All), so a page can end one layer and start the next.
  const [layerPageRows, setLayerPageRows] = useState<{ layer: EditableLayerSummary; row: EditableLayerRow }[]>([]);
  const [layerLoading, setLayerLoading] = useState(false);
  useEffect(() => {
    const start = (page - 1) * pageSize;
    const end = page * pageSize;
    const wanted: { layer: EditableLayerSummary; offset: number; limit: number }[] = [];
    let at = showingLayers ? 0 : features.length;
    for (const l of layersListed ? layers : []) {
      const n = layerInfo.totals[l.id] ?? 0;
      const from = Math.max(start, at);
      const to = Math.min(end, at + n);
      if (to > from) wanted.push({ layer: l, offset: from - at, limit: to - from });
      at += n;
    }
    if (wanted.length === 0) {
      setLayerPageRows([]);
      return;
    }
    let cancelled = false;
    setLayerLoading(true);
    Promise.all(
      wanted.map((w) =>
        listEditableLayerRows(scope.projectId ?? w.layer.project_id, w.layer.id, {
          zoneId: scope.zoneId,
          wardId: scope.wardId,
          blockId: scope.blockId,
          q: layerQuery,
          qColumn: layerColumn,
          updatedFrom,
          updatedTo,
          updatedBy,
          offset: w.offset,
          limit: w.limit,
        }).then((d) => d.rows.map((row) => ({ layer: w.layer, row })))
      )
    )
      .then((parts) => {
        if (!cancelled) setLayerPageRows(parts.flat());
      })
      .catch(() => {
        if (!cancelled) setLayerError(t.layerRowsTable.loadFailed);
      })
      .finally(() => {
        if (!cancelled) setLayerLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerInfo, page, pageSize, showingLayers, layersListed, features.length]);

  const isStaff = (getStoredUser()?.role ?? "surveyor") !== "surveyor";

  const [deletingId, setDeletingId] = useState<number | null>(null);
  // The shape awaiting confirmation. This delete had no prompt at all, which
  // made a surveyor's collected geometry the easiest thing in the portal to
  // destroy by accident -- one stray click, no undo, and the surveyor has to
  // walk back to the property to redraw it.
  const [confirming, setConfirming] = useState<FieldFeature | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Rows whose revisit checkbox is being saved. */
  const [revisitBusy, setRevisitBusy] = useState<Set<number>>(new Set());

  async function toggleRevisit(feature: FieldFeature, next: boolean) {
    setRevisitBusy((prev) => new Set(prev).add(feature.id));
    setError(null);
    try {
      await setFeatureRevisit(feature.id, next);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : t.featureTable.revisitFailed);
    } finally {
      setRevisitBusy((prev) => {
        const out = new Set(prev);
        out.delete(feature.id);
        return out;
      });
    }
  }

  /**
   * Cells edited but not yet written, keyed by feature id.
   *
   * Keyed by id rather than row index because this table re-sorts and
   * refetches under itself on a poll -- an index would silently start
   * pointing at a different property mid-edit.
   */
  const [edits, setEdits] = useState<Record<number, RowEdits>>({});
  const [saving, setSaving] = useState(false);
  /**
   * Cells are read-only until this is on -- Edit is a deliberate step, not
   * something a stray click into a table falls into. Off automatically once
   * Save succeeds (see handleSave); staying on after saving read as "still
   * editing" when nothing further was intended.
   */
  const [editMode, setEditMode] = useState(false);

  // Columns come from whatever attribute keys are actually present, so the
  // table adapts to what field staff record rather than assuming a fixed set.
  //
  // plot_no is excluded because it has a fixed column of its own below. It used
  // to rely on this, and so appeared only once some feature already carried
  // one -- on a project whose imported outlines carry {id, area_m2, perim_m}
  // the column was simply absent, which reads as "the portal cannot show plot
  // numbers" rather than "nobody has recorded one yet". It is a field the app
  // now collects deliberately, so the office should see it waiting to be
  // filled.
  const { propertyColumns, hasRevisit } = useMemo(() => {
    // Layer box: the layer's own attributes, in its order, less the ones
    // shown as Property no. / Plot no.
    if (showingLayers) {
      return {
        propertyColumns: layerInfo.columns.filter(
          (c) => !PROPERTY_NO_KEYS.has(normKey(c)) && !PLOT_NO_KEYS.has(normKey(c))
        ),
        hasRevisit: false,
      };
    }
    const keys = new Set<string>();
    let revisit = false;
    features.forEach((f) =>
      Object.keys(f.properties || {}).forEach((k) => {
        if (REVISIT_KEYS.has(k)) revisit = true;
        else if (k !== "plot_no" && k !== "survey_name") keys.add(k);
      })
    );
    const surveyColumns = Array.from(keys).sort();
    if (!layersListed) return { propertyColumns: surveyColumns, hasRevisit: revisit };
    // All: layer attributes after the survey ones, in the layer's own order;
    // a name both share is one column.
    const layerColumns = layerInfo.columns.filter(
      (c) => !keys.has(c) && !PROPERTY_NO_KEYS.has(normKey(c)) && !PLOT_NO_KEYS.has(normKey(c))
    );
    return { propertyColumns: [...surveyColumns, ...layerColumns], hasRevisit: revisit };
  }, [features, layerInfo.columns, showingLayers, layersListed]);

  // The search box's column list for the box on show. Survey: the fixed
  // columns, then each attribute. Layers: any attribute, or one of them.
  // All: any column, the two numbers both kinds have, then every attribute.
  const searchColumns: { value: string; label: string }[] = combined
    ? [
        { value: "", label: t.featureTable.allColumns },
        { value: "property_no", label: t.featureTable.colPropertyNo },
        { value: "plot_no", label: t.featureTable.colPlotNo },
        ...[
          ...surveyKeys,
          ...layerInfo.columns.filter(
            (c) => !surveyKeys.includes(c) && !PROPERTY_NO_KEYS.has(normKey(c)) && !PLOT_NO_KEYS.has(normKey(c))
          ),
        ].map((c) => ({ value: c, label: c.replace(/_/g, " ") })),
      ]
    : showingLayers
    ? [
        { value: "", label: t.featureTable.allColumns },
        ...layerInfo.columns.map((c) => ({ value: c, label: c.replace(/_/g, " ") })),
      ]
    : [
        { value: "property_no", label: t.featureTable.colPropertyNo },
        { value: "partition", label: t.featureTable.colPartition },
        { value: "plot_no", label: t.featureTable.colPlotNo },
        { value: "created_by", label: t.featureTable.colCreatedBy },
        ...surveyKeys.map((c) => ({ value: c, label: c.replace(/_/g, " ") })),
      ];
  const searchColumnLabel =
    searchColumns.find((c) => c.value === searchColumn)?.label ?? searchColumn.replace(/_/g, " ");

  const changedCount = Object.keys(edits).length;

  // --- reading a cell: the pending edit if there is one, else what's stored ---

  function storedText(feature: FieldFeature, field: "property_no" | "partition"): string {
    return feature[field] ?? "";
  }

  function storedProperty(feature: FieldFeature, key: string): string {
    const raw = feature.properties?.[key];
    if (raw === null || raw === undefined) return "";
    return typeof raw === "object" ? JSON.stringify(raw) : String(raw);
  }

  function fieldValue(feature: FieldFeature, field: "property_no" | "partition"): string {
    const edited = edits[feature.id]?.[field];
    return edited !== undefined ? edited : storedText(feature, field);
  }

  function propertyValue(feature: FieldFeature, key: string): string {
    const edited = edits[feature.id]?.properties?.[key];
    return edited !== undefined ? edited : storedProperty(feature, key);
  }

  // --- writing a cell, dropping the entry again if it matches what's stored ---

  function pruneRow(row: RowEdits): RowEdits | null {
    const props = row.properties && Object.keys(row.properties).length ? row.properties : undefined;
    const next: RowEdits = {
      ...(row.property_no !== undefined ? { property_no: row.property_no } : {}),
      ...(row.partition !== undefined ? { partition: row.partition } : {}),
      ...(props ? { properties: props } : {}),
    };
    return Object.keys(next).length ? next : null;
  }

  function setField(
    feature: FieldFeature,
    field: "property_no" | "partition",
    next: string
  ) {
    setEdits((prev) => {
      const row: RowEdits = { ...(prev[feature.id] ?? {}) };
      if (next === storedText(feature, field)) delete row[field];
      else row[field] = next;

      const out = { ...prev };
      const pruned = pruneRow(row);
      if (pruned) out[feature.id] = pruned;
      else delete out[feature.id];
      return out;
    });
  }

  function setProperty(feature: FieldFeature, key: string, next: string) {
    setEdits((prev) => {
      const row: RowEdits = { ...(prev[feature.id] ?? {}) };
      const props = { ...(row.properties ?? {}) };
      if (next === storedProperty(feature, key)) delete props[key];
      else props[key] = next;
      row.properties = props;

      const out = { ...prev };
      const pruned = pruneRow(row);
      if (pruned) out[feature.id] = pruned;
      else delete out[feature.id];
      return out;
    });
  }

  function isFieldEdited(feature: FieldFeature, field: "property_no" | "partition") {
    return edits[feature.id]?.[field] !== undefined;
  }

  function isPropertyEdited(feature: FieldFeature, key: string) {
    return edits[feature.id]?.properties?.[key] !== undefined;
  }

  /**
   * Writes every edited row, one PATCH each.
   *
   * Sequential rather than parallel, and it stops at the first refusal: the
   * house-number rule is checked per request against the rows already stored,
   * so firing a batch at once can have two of them race for the same number
   * and both pass. Stopping also leaves the remaining edits in the table for
   * the operator to fix, rather than reporting one error for a batch where
   * some rows landed and some didn't with no way to tell which.
   */
  async function handleSave() {
    setSaving(true);
    setError(null);

    const entries = Object.entries(edits);
    let saved = 0;
    try {
      for (const [id, row] of entries) {
        const featureId = Number(id);
        const feature = features.find((f) => f.id === featureId);
        if (!feature) continue;

        const payload: {
          property_no?: string | null;
          partition?: string | null;
          properties?: Record<string, unknown>;
        } = {};

        // Sent as typed, including "" -- NOT converted to null. The backend
        // reads an absent/null field as "leave this alone", so blanking a
        // house number with null would silently do nothing; "" reaches the
        // rule and comes back as a plain 422 saying a number is required.
        // Blanking a partition through the same path legitimately clears it
        // (normalise_partition collapses "" to NULL).
        if (row.property_no !== undefined) payload.property_no = row.property_no;
        if (row.partition !== undefined) payload.partition = row.partition;

        if (row.properties) {
          // The whole bag, merged: PATCH replaces `properties` wholesale, so
          // sending only the edited keys would delete every attribute the row
          // already carried.
          const merged: Record<string, unknown> = { ...(feature.properties ?? {}) };
          for (const [key, text] of Object.entries(row.properties)) {
            merged[key] = coerceLike(feature.properties?.[key], text);
          }
          payload.properties = merged;
        }

        await updateFeature(featureId, payload);
        saved += 1;
        // Dropped as each one lands, so a failure part-way leaves exactly the
        // rows that still need attention.
        setEdits((prev) => {
          const out = { ...prev };
          delete out[featureId];
          return out;
        });
      }
      // Every row landed with nothing left pending: leave edit mode, the same
      // place Discard returns to. A partial save (caught below) stays in edit
      // mode instead -- the remaining rows still need attention, and dropping
      // back to read-only would hide edits that are still sitting there.
      setEditMode(false);
    } catch (e) {
      const detail = e instanceof Error ? e.message : "Couldn't save those changes.";
      setError(
        saved > 0
          ? `Saved ${saved} row${saved === 1 ? "" : "s"}, then stopped: ${detail}`
          : detail
      );
    } finally {
      setSaving(false);
      if (saved > 0) onChanged();
    }
  }

  async function handleDelete(id: number) {
    setDeletingId(id);
    setError(null);
    try {
      await deleteFeature(id);
      setConfirming(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't delete that shape.");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleExport() {
    setExporting(true);
    setError(null);
    try {
      await downloadShapefile(scope);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.featureTable.exportFailed);
    } finally {
      setExporting(false);
    }
  }

  // Type, Location, Property no., Partition, Plot no., Revisit, Survey Start,
  // Surveyed At, Duration, Created At, Updated At, Actions -- the fixed
  // columns either side of the dynamic ones.
  const showHierarchy = hierarchy.zones.length > 0 || hierarchy.wards.length > 0;
  const columnCount = showingLayers
    ? 6 + (showHierarchy ? 1 : 0) + propertyColumns.length
    : 9 + (isStaff ? 1 : 0) + (showHierarchy ? 1 : 0) + (hasRevisit ? 1 : 0) + propertyColumns.length;
  // Each header cell carries the background itself: a sticky <thead>'s own
  // background is not painted behind its cells, so rows showed through it.
  const th = "whitespace-nowrap bg-surface-sunken px-2 py-2 font-semibold";
  const td = "whitespace-nowrap px-2 py-1.5 text-ink-secondary";

  const cellClass = (edited: boolean) =>
    clsx(
      "w-full min-w-[5rem] rounded border bg-transparent px-1 py-0.5 text-[11px] text-ink",
      "outline-none transition focus:border-accent focus:bg-surface",
      "disabled:cursor-not-allowed disabled:opacity-60",
      edited ? "border-accent bg-accent-subtle/40" : "border-transparent hover:border-line-strong"
    );

  return (
    <Panel
      title={
        // Two boxes, one per kind of row; the table lists the one picked.
        <div className="flex flex-wrap items-center gap-2" role="tablist">
          {(
            [
              ...(layers.length > 0
                ? [
                    {
                      key: "all" as TableView,
                      label: t.featureTable.tabAll,
                      count: (features.length + (combined && revisitOnly ? 0 : layerTotal)).toLocaleString(),
                    },
                  ]
                : []),
              {
                key: "field" as TableView,
                label: t.featureTable.tabFieldData,
                count: allFeatures.length
                  ? narrowed
                    ? `${features.length.toLocaleString()} of ${allFeatures.length.toLocaleString()}`
                    : features.length.toLocaleString()
                  : "0",
              },
              ...(layers.length > 0
                ? [{ key: "layers" as TableView, label: t.featureTable.tabLayers, count: layerTotal.toLocaleString() }]
                : []),
            ]
          ).map((box) => {
            const active = box.key === (showingLayers ? "layers" : combined ? "all" : "field");
            return (
              <button
                key={box.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => switchView(box.key)}
                disabled={!active && editMode}
                title={!active && editMode ? t.featureTable.finishEditingFirst : undefined}
                className={clsx(
                  "flex items-baseline gap-2 rounded-md border px-3 py-1.5 font-display text-[13px] font-semibold uppercase tracking-[0.05em] transition",
                  active
                    ? "border-accent-solid bg-accent-subtle text-ink"
                    : "border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink",
                  "disabled:cursor-not-allowed disabled:opacity-50"
                )}
              >
                {box.label}
                <span className="tabular text-xs font-medium normal-case tracking-normal">{box.count}</span>
              </button>
            );
          })}
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          {layerLoading && <Spinner className="h-3 w-3" />}
          {isStaff && !showingLayers && (editMode ? (
            <>
              {changedCount > 0 && (
                <>
                  <span className="text-2xs text-status-warning">
                    {changedCount} row{changedCount === 1 ? "" : "s"} edited
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setEdits({})} disabled={saving}>
                    <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                    {t.featureTable.discard}
                  </Button>
                  <Button size="sm" variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <Spinner className="mr-1.5 h-3 w-3" />
                    ) : (
                      <Save className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {saving ? "Saving…" : t.featureTable.save}
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditMode(false)}
                disabled={changedCount > 0 || saving}
                title={changedCount > 0 ? t.featureTable.saveOrDiscardFirst : undefined}
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                Done
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setEditMode(true)}
              disabled={locked || features.length === 0}
              title={locked ? lockedReason : undefined}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {t.featureTable.edit}
            </Button>
          ))}
          {exportScopeNote && (
            <span
              className="max-w-[14rem] text-2xs leading-tight text-status-warning"
              title={exportScopeNote}
            >
              {exportScopeNote}
            </span>
          )}
          {isStaff && (
            <Button size="sm" onClick={handleExport} disabled={exporting || (allFeatures.length === 0 && layers.length === 0)}>
              {exporting ? <Spinner className="h-3 w-3" /> : null}
              {exporting ? "Preparing…" : t.featureTable.exportShapefile}
            </Button>
          )}
        </div>
      }
      className="h-full min-h-0"
      bodyClassName="flex flex-col min-h-0"
    >
      {(allFeatures.length > 0 || layers.length > 0) && (
        // The filters on a row of their own under the title, wrapping on a
        // narrow screen, so the title bar keeps only Edit and Export.
        <div className="flex flex-none flex-wrap items-center gap-2 border-b border-line px-3 py-1.5">
          {/* Search one column: pick it, then type. */}
          <div className="flex h-7 items-stretch">
            <select
              value={searchColumn}
              onChange={(e) => setSearchColumn(e.target.value)}
              title={t.featureTable.searchColumnTitle}
              aria-label={t.featureTable.searchColumnTitle}
              className="max-w-[9rem] rounded-l-md border border-r-0 border-line bg-surface-sunken px-1.5 text-xs text-ink focus:outline-none focus:ring-1 focus:ring-accent-solid"
            >
              {searchColumns.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted" />
              <input
                type="text"
                inputMode="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setSearch("");
                }}
                placeholder={t.featureTable.searchIn(searchColumnLabel)}
                aria-label={t.featureTable.searchIn(searchColumnLabel)}
                className="h-full w-40 rounded-r-md border border-line bg-surface-sunken pl-7 pr-7 text-xs text-ink placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-accent-solid"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  title={t.featureTable.clearSearch}
                  aria-label={t.featureTable.clearSearch}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-ink-muted hover:text-ink"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="flex h-7 items-center gap-1 text-2xs text-ink-muted" title={t.featureTable.dateFilterTitle}>
            <label className="flex items-center gap-1">
              {t.featureTable.dateFrom}
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                aria-label={t.featureTable.dateFrom}
                className="h-7 w-[8.5rem] rounded-md border border-line bg-surface-sunken px-1.5 text-xs text-ink focus:outline-none focus:ring-1 focus:ring-accent-solid"
              />
            </label>
            <label className="flex items-center gap-1">
              {t.featureTable.dateTo}
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                aria-label={t.featureTable.dateTo}
                className="h-7 w-[8.5rem] rounded-md border border-line bg-surface-sunken px-1.5 text-xs text-ink focus:outline-none focus:ring-1 focus:ring-accent-solid"
              />
            </label>
            {dated && (
              <button
                type="button"
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                title={t.featureTable.clearDates}
                aria-label={t.featureTable.clearDates}
                className="rounded p-0.5 text-ink-muted hover:text-ink"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {surveyors.length > 0 && (
            <select
              value={surveyor}
              onChange={(e) => setSurveyor(e.target.value)}
              title={t.featureTable.surveyorFilterTitle}
              aria-label={t.featureTable.surveyorFilterTitle}
              className={clsx(
                "h-7 max-w-[11rem] rounded-md border bg-surface-sunken px-1.5 text-xs text-ink focus:outline-none focus:ring-1 focus:ring-accent-solid",
                surveyor ? "border-accent-solid" : "border-line"
              )}
            >
              <option value="">{t.featureTable.allSurveyors}</option>
              {surveyors.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
          {allFeatures.length > 0 && !showingLayers && (
            <label
              className="flex h-7 cursor-pointer select-none items-center gap-1.5 rounded-md border border-line bg-surface-sunken px-2.5 text-xs text-ink"
              title={t.featureTable.revisitOnlyTitle}
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5 cursor-pointer accent-accent-solid"
                checked={revisitOnly}
                onChange={(e) => setRevisitOnly(e.target.checked)}
              />
              {t.featureTable.revisitOnly}
              <span className="tabular text-2xs text-ink-muted">{revisitTotal}</span>
            </label>
          )}
        </div>
      )}

      {error && (
        <div className="px-3 pt-3">
          <Banner tone="danger" action={<Button size="sm" onClick={() => setError(null)}>Dismiss</Button>}>
            {error}
          </Banner>
        </div>
      )}

      {locked && lockedReason && (
        <div className="px-3 pt-3">
          <Banner tone="info">{lockedReason}</Banner>
        </div>
      )}

      {layerError && (
        <div className="px-3 pt-3">
          <Banner tone="danger" action={<Button size="sm" onClick={() => setLayerError(null)}>Dismiss</Button>}>
            {layerError}
          </Banner>
        </div>
      )}

      {grandTotal > 0 ? null : showingLayers ? (
        query || dated || surveyor ? (
          <EmptyState title={t.featureTable.noLayerMatchTitle} body={t.featureTable.noLayerMatchBody} />
        ) : (
          <EmptyState title={t.featureTable.noLayerShapesTitle} body={t.featureTable.noLayerShapesBody} />
        )
      ) : surveyor && !dated && !query && !revisitOnly ? (
        <EmptyState title={t.featureTable.noSurveyorMatchTitle} body={t.featureTable.noSurveyorMatchBody(surveyor)} />
      ) : dated && !query && !revisitOnly ? (
        <EmptyState title={t.featureTable.noDateMatchTitle} body={t.featureTable.noDateMatchBody} />
      ) : allFeatures.length > 0 && features.length === 0 ? (
        revisitOnly && !query ? (
          <EmptyState title={t.featureTable.noRevisitTitle} body={t.featureTable.noRevisitBody} />
        ) : (
          <EmptyState title={t.featureTable.noMatchTitle} body={
              searchColumn
                ? t.featureTable.noMatchBody(searchColumnLabel, search.trim())
                : t.featureTable.noMatchAnyBody(search.trim())
            } />
        )
      ) : (
        <EmptyState
          title={t.featureTable.noShapesYetTitle}
          body="Points and building outlines recorded by surveyors in the field will appear here as they sync."
        />
      )}
      {grandTotal > 0 && (
        // Bounded height so the header can stick and rows scroll in place; the
        // !overscroll override lets the wheel carry on to the page at either end
        // (.thin-scroll's "contain" otherwise swallows it, because this box also
        // scrolls horizontally).
        <div className="thin-scroll min-h-0 max-h-[70vh] flex-1 overflow-auto !overscroll-y-auto">
          <table className="w-full border-collapse text-left text-[11px] leading-tight">
            <thead className="sticky top-0 z-10 bg-surface-sunken text-2xs uppercase tracking-wider text-ink-muted shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">
              <tr>
                <th className={th}>Type</th>
                {isStaff && !showingLayers && <th className={th}>{t.featureTable.colMarkRevisit}</th>}
                {/* Where the shape sits in the survey. Read-only in every
                    mode: a property's zone/ward/block follow from the block
                    it was collected in, so changing them here would mean
                    moving the shape, not editing a field. */}
                {showHierarchy && <th className={th}>Location</th>}
                {/* Stored as `property_no` end to end (house_number until
                    2026-10-14, see deploy/rename-house-number-to-property-no.sql). */}
                <th className={th}>Property no.</th>
                {!showingLayers && <th className={th}>Partition</th>}
                <th className={th}>Plot no.</th>
                {propertyColumns.map((col) => (
                  <th key={col} className={th}>
                    {col.replace(/_/g, " ")}
                  </th>
                ))}
                {hasRevisit && <th className={th}>Revisit</th>}
                {!showingLayers && (
                  <>
                    <th className={th}>Survey Start</th>
                    <th className={th}>Surveyed At</th>
                    <th className={th}>Duration</th>
                  </>
                )}
                <th className={th}>Created At</th>
                <th className={th}>Updated At</th>
                <th className="sticky right-0 z-20 whitespace-nowrap bg-surface-sunken px-2 py-2 text-right font-semibold shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedFeatures.map((feature) => (
                <tr
                  key={feature.id}
                  className={clsx(
                    "border-t border-line transition",
                    feature.id === selectedId
                      ? "bg-accent-subtle/60"
                      : "odd:bg-surface even:bg-surface-sunken/30 hover:bg-surface-sunken/70"
                  )}
                >
                  {/* Geometry type is not editable here: the shape itself is
                      corrected in the field, standing at the property. */}
                  <td className={clsx(td, "text-ink-muted")}>
                    {feature.geometry?.type ?? "—"}
                  </td>
                  {isStaff && (
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer accent-accent-solid disabled:cursor-wait"
                        checked={isRevisitActive(feature.properties)}
                        disabled={revisitBusy.has(feature.id)}
                        onChange={(e) => void toggleRevisit(feature, e.target.checked)}
                        title={t.featureTable.markRevisitTitle}
                        aria-label={`${t.featureTable.colMarkRevisit}, ${feature.property_no || feature.id}`}
                      />
                    </td>
                  )}
                  {showHierarchy &&
                    (() => {
                      const at = ancestryOf(feature, hierarchy);
                      return (
                        <td className="whitespace-nowrap px-2 py-1.5">
                          <div className="font-medium text-ink">{at.block ?? "—"}</div>
                          <div className="text-2xs text-ink-muted">
                            {[at.zone, at.ward].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </td>
                      );
                    })()}
                  {editMode ? (
                    <>
                      <td className="px-1.5 py-0.5">
                        <input
                          value={fieldValue(feature, "property_no")}
                          onChange={(e) => setField(feature, "property_no", e.target.value)}
                          disabled={locked || saving}
                          title={locked ? lockedReason : undefined}
                          aria-label={`Property number, feature ${feature.id}`}
                          className={cellClass(isFieldEdited(feature, "property_no"))}
                        />
                      </td>
                      <td className="px-1.5 py-0.5">
                        <input
                          value={fieldValue(feature, "partition")}
                          onChange={(e) => setField(feature, "partition", e.target.value)}
                          disabled={locked || saving}
                          title={locked ? lockedReason : undefined}
                          aria-label={`Partition, feature ${feature.id}`}
                          className={cellClass(isFieldEdited(feature, "partition"))}
                        />
                      </td>
                      {/* From the attribute bag rather than a column: plot_no
                          is whatever the source register called it, carried
                          through the import and editable in the app. Blank
                          until a surveyor records one. */}
                      <td className="px-1.5 py-0.5">
                        <input
                          value={propertyValue(feature, "plot_no")}
                          onChange={(e) => setProperty(feature, "plot_no", e.target.value)}
                          disabled={locked || saving}
                          title={locked ? lockedReason : undefined}
                          aria-label={`Plot number, feature ${feature.id}`}
                          className={cellClass(isPropertyEdited(feature, "plot_no"))}
                        />
                      </td>
                      {propertyColumns.map((col) => (
                        <td key={col} className="px-1.5 py-0.5">
                          <input
                            value={propertyValue(feature, col)}
                            onChange={(e) => setProperty(feature, col, e.target.value)}
                            disabled={locked || saving}
                            title={locked ? lockedReason : undefined}
                            aria-label={`${col}, feature ${feature.id}`}
                            className={cellClass(isPropertyEdited(feature, col))}
                          />
                        </td>
                      ))}
                    </>
                  ) : (
                    <>
                      <td className={clsx(td, "font-semibold text-ink")}>
                        {feature.property_no || "—"}
                      </td>
                      <td className={td}>{feature.partition || "—"}</td>
                      <td className={td}>
                        {String(feature.properties?.plot_no ?? "").trim() || "—"}
                      </td>
                      {propertyColumns.map((col) => {
                        const text = displayValue(feature.properties?.[col]);
                        return (
                          <td key={col} className={clsx(td, "max-w-[16rem] truncate")} title={text}>
                            {text}
                          </td>
                        );
                      })}
                    </>
                  )}
                  {hasRevisit && (
                    <td className="px-2 py-1.5">
                      <RevisitCell properties={feature.properties} />
                    </td>
                  )}
                  <td className="whitespace-nowrap px-2 py-1.5 text-ink-secondary">
                    {/* When the surveyor opened the property form in the app. Older
                        records (and ones numbered before this was recorded) have
                        none, so they show a dash rather than some other time. */}
                    <div>
                      {feature.survey_started_at
                        ? parseApiDate(feature.survey_started_at).toLocaleString("en-IN", {
                            timeZone: DISPLAY_TIME_ZONE,
                            day: "2-digit",
                            month: "short",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : <span className="text-ink-muted">—</span>}
                    </div>
                    {feature.created_by && (
                      <div className="text-2xs text-ink-muted">{feature.created_by}</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-ink-secondary">
                    {feature.surveyed_at
                      ? parseApiDate(feature.surveyed_at).toLocaleString("en-IN", {
                          timeZone: DISPLAY_TIME_ZONE,
                          day: "2-digit",
                          month: "short",
                          year: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : <span className="text-ink-muted">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-ink-secondary text-right tabular-nums">
                    {feature.survey_duration_seconds != null
                      ? formatPropertyDuration(feature.survey_duration_seconds)
                      : <span className="text-ink-muted">—</span>}
                  </td>
                  {/* The row's own timestamps from the database: when it was
                      first stored, and when it last changed (any edit). */}
                  <td className="whitespace-nowrap px-2 py-1.5 text-ink-secondary">
                    {formatDateTime(feature.created_at) ?? <span className="text-ink-muted">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-ink-secondary">
                    {formatDateTime(feature.updated_at) ?? <span className="text-ink-muted">—</span>}
                  </td>
                  <td className={clsx(
                    "sticky right-0 z-[5] whitespace-nowrap px-2 py-1.5 text-right shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]",
                    feature.id === selectedId ? "bg-accent-subtle/60" : "bg-surface"
                  )}>
                    {/* Icon, not a "Zoom" label: this column already carries
                        Delete, and the table is horizontally scrollable at
                        realistic column counts -- a second word of button
                        pushes Delete off the visible edge on a laptop. */}
                    {onZoomTo && (
                      <button
                        type="button"
                        onClick={() => onZoomTo(feature)}
                        disabled={!feature.geometry}
                        title={
                          feature.geometry
                            ? "Zoom to this shape on the map"
                            : "This row has no geometry to zoom to"
                        }
                        aria-label={`Zoom to ${
                          propertyLabel(feature.property_no, feature.partition) || "this shape"
                        }`}
                        className={clsx(
                          "mr-1.5 inline-flex h-6 w-6 items-center justify-center rounded align-middle transition",
                          feature.id === selectedId
                            ? "bg-accent-solid text-white"
                            : "text-ink-muted hover:bg-surface-sunken hover:text-ink",
                          !feature.geometry && "cursor-not-allowed opacity-40"
                        )}
                      >
                        <Crosshair className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {isStaff && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setConfirming(feature)}
                        disabled={locked || deletingId === feature.id}
                        title={locked ? lockedReason : undefined}
                      >
                        {deletingId === feature.id ? "Removing…" : t.featureTable.delete}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {layerPageRows.map(({ layer, row }) => {
                const selected =
                  selectedLayerShape?.layerId === layer.id && selectedLayerShape.position === row.position;
                return (
                  <tr
                    key={`layer-${layer.id}-${row.position}`}
                    className={clsx(
                      "border-t border-line transition",
                      selected ? "bg-accent-subtle/60" : "bg-surface-sunken/40 hover:bg-surface-sunken/70"
                    )}
                  >
                    <td className={clsx(td, "text-ink-muted")}>
                      <div>{row.geometry_type}</div>
                      <span
                        className="mt-0.5 inline-block rounded bg-accent-subtle px-1.5 py-px text-2xs font-medium text-ink"
                        title={t.layerRowsTable.badgeTitle(layer.name)}
                      >
                        {t.layerRowsTable.badge}
                      </span>
                      <div className="mt-0.5 max-w-[10rem] truncate text-2xs" title={layer.name}>
                        {layer.name} #{row.position + 1}
                      </div>
                    </td>
                    {/* In All, blanks under the survey-only columns. */}
                    {isStaff && !showingLayers && <td className={td} />}
                    {showHierarchy &&
                      (() => {
                        const at = ancestryOf(
                          { block_id: row.block_id ?? null, ward_id: row.ward_id ?? null, zone_id: null },
                          hierarchy
                        );
                        return (
                          <td className="whitespace-nowrap px-2 py-1.5">
                            <div className="font-medium text-ink">{at.block ?? "—"}</div>
                            <div className="text-2xs text-ink-muted">
                              {[at.zone, at.ward].filter(Boolean).join(" · ") || "—"}
                            </div>
                          </td>
                        );
                      })()}
                    <td className={clsx(td, "font-semibold text-ink")}>
                      {layerValue(row.properties, PROPERTY_NO_KEYS)}
                    </td>
                    {!showingLayers && <td className={clsx(td, "text-ink-muted")}>—</td>}
                    <td className={td}>{layerValue(row.properties, PLOT_NO_KEYS)}</td>
                    {propertyColumns.map((col) => {
                      const text = displayValue(row.properties[col]);
                      return (
                        <td key={col} className={clsx(td, "max-w-[16rem] truncate")} title={text}>
                          {text}
                        </td>
                      );
                    })}
                    {!showingLayers && (
                      <>
                        {hasRevisit && <td className={td} />}
                        <td className={clsx(td, "text-ink-muted")}>—</td>
                        <td className={clsx(td, "text-ink-muted")}>—</td>
                        <td className={clsx(td, "text-ink-muted")}>—</td>
                      </>
                    )}
                    {/* When the shape was added / last changed, with who did it
                        underneath (as Survey Start shows the surveyor). */}
                    <td className="whitespace-nowrap px-2 py-1.5 text-ink-secondary">
                      {formatDateTime(row.created_at) ?? <span className="text-ink-muted">—</span>}
                      {row.created_by && <div className="text-2xs text-ink-muted">{row.created_by}</div>}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-ink-secondary">
                      {formatDateTime(row.updated_at) ?? <span className="text-ink-muted">—</span>}
                      {row.updated_by && <div className="text-2xs text-ink-muted">{row.updated_by}</div>}
                    </td>
                    <td
                      className={clsx(
                        "sticky right-0 z-[5] whitespace-nowrap px-2 py-1.5 text-right shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]",
                        selected ? "bg-accent-subtle/60" : "bg-surface"
                      )}
                    >
                      {onZoomToLayerShape && (
                        <button
                          type="button"
                          onClick={() => onZoomToLayerShape(layer.id, row.position)}
                          title={t.featureTable.zoomToShapeTitle}
                          aria-label={t.featureTable.zoomToAriaLabel(`${layer.name} #${row.position + 1}`)}
                          className={clsx(
                            "inline-flex h-6 w-6 items-center justify-center rounded align-middle transition",
                            selected ? "bg-accent-solid text-white" : "text-ink-muted hover:bg-surface-sunken hover:text-ink"
                          )}
                        >
                          <Crosshair className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="sr-only">{columnCount} columns</p>
        </div>
      )}

      {/* Pagination bar — always shown when there is more than one page's worth of data */}
      {grandTotal > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line px-3 py-2">
          {/* Row range */}
          <span className="text-2xs text-ink-muted tabular-nums">
            {`${((page - 1) * pageSize + 1).toLocaleString()}–${Math.min(
              page * pageSize,
              grandTotal
            ).toLocaleString()} of ${grandTotal.toLocaleString()}`}
          </span>

          {/* Page navigation */}
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              title="First page"
              className="flex h-6 w-6 items-center justify-center rounded text-xs text-ink-muted transition hover:bg-surface-sunken hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
            >
              «
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1}
              title="Previous page"
              className="flex h-6 w-6 items-center justify-center rounded text-xs text-ink-muted transition hover:bg-surface-sunken hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
            >
              ‹
            </button>
            <span className="min-w-[5rem] text-center text-2xs text-ink-muted tabular-nums">
              {page} / {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page === pageCount}
              title="Next page"
              className="flex h-6 w-6 items-center justify-center rounded text-xs text-ink-muted transition hover:bg-surface-sunken hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(pageCount)}
              disabled={page === pageCount}
              title="Last page"
              className="flex h-6 w-6 items-center justify-center rounded text-xs text-ink-muted transition hover:bg-surface-sunken hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
            >
              »
            </button>
          </div>

          {/* Page size */}
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="h-6 rounded border border-line bg-surface-sunken px-1.5 text-2xs text-ink focus:outline-none focus:ring-1 focus:ring-accent-solid"
            aria-label="Rows per page"
          >
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={t.featureTable.deleteThisShapeTitle}
        body={
          <>
            {confirming?.property_no ? (
              <>
                Property number{" "}
                <span className="font-medium text-ink">
                  {propertyLabel(confirming.property_no, confirming.partition)}
                </span>{" "}
              </>
            ) : (
              <>This {confirming?.geometry?.type ?? t.featureTable.thisShapeFallback} </>
            )}
            was collected in the field and cannot be recovered — someone would
            have to return to the property and record it again.
          </>
        }
        confirmLabel={t.featureTable.deleteShapeConfirmLabel}
        busy={deletingId !== null}
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && void handleDelete(confirming.id)}
      />
    </Panel>
  );
}
